import type {
  CoachContent,
  PublicDirectory,
  PublicPlace,
  PublicService,
  PublicSpecialist,
  ServiceContent,
} from "../types";

const normalize = (value: string) => value.trim().toLowerCase();

/** Booking services a service page sells, in the order the page names them. */
export const servicesFor = (content: ServiceContent, directory: PublicDirectory | null): PublicService[] => {
  if (!directory) return [];
  return content.bookingServices.flatMap((name) =>
    directory.services.filter((service) => normalize(service.name) === name),
  );
};

/** Coaches who offer at least one of the given services. */
export const specialistsFor = (services: PublicService[], directory: PublicDirectory | null): PublicSpecialist[] => {
  if (!directory) return [];
  const ids = new Set(services.map((service) => service.id));
  return directory.specialists.filter((specialist) => specialist.serviceIds.some((id) => ids.has(id)));
};

export const coachContentFor = (specialist: PublicSpecialist, coaches: CoachContent[]) =>
  coaches.find((coach) => coach.name === normalize(specialist.name)) ?? null;

/** Open start times of one coach, soonest first. */
export const openTimes = (specialist: PublicSpecialist) =>
  specialist.days.flatMap((day) => day.slots.filter((slot) => slot.available).map((slot) => slot.startAt));

/** The soonest time a coach can be booked: inside the grid, or the pack's look-ahead past it. */
export const nextOpenTime = (specialist: PublicSpecialist) => openTimes(specialist)[0] ?? specialist.nextAvailable;

/** The soonest open time across several coaches. */
export const earliestOpenTime = (specialists: PublicSpecialist[]) =>
  specialists
    .map(nextOpenTime)
    .filter((time): time is string => Boolean(time))
    .toSorted()[0] ?? null;

export const formatTime = (startAt: string, timeZone: string, locale?: string) =>
  new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone }).format(
    new Date(startAt),
  );

/** "Today 10:20" for today, "Tue 09:00" for any other day. */
export const formatOpenTime = (
  startAt: string,
  {
    timeZone,
    todayLabel,
    now = new Date(),
    locale,
  }: { timeZone: string; todayLabel: string; now?: Date; locale?: string },
) => {
  const dayKey = (date: Date) => new Intl.DateTimeFormat("en-CA", { timeZone }).format(date);
  const date = new Date(startAt);
  const day =
    dayKey(date) === dayKey(now)
      ? todayLabel
      : new Intl.DateTimeFormat(locale, { weekday: "short", timeZone }).format(date);
  return `${day} ${formatTime(startAt, timeZone, locale)}`;
};

/** The coach who can be booked soonest, and when. Null when nobody has an open time. */
export const soonestOpening = (specialists: PublicSpecialist[]) =>
  specialists
    .map((specialist) => ({ specialist, startAt: nextOpenTime(specialist) }))
    .filter((entry): entry is { specialist: PublicSpecialist; startAt: string } => Boolean(entry.startAt))
    .toSorted((a, b) => a.startAt.localeCompare(b.startAt))[0] ?? null;

/** How much of a coach's week on the grid is still open: free start times out of all of them. */
export const openShare = (specialist: PublicSpecialist) => {
  const slots = specialist.days.flatMap((day) => day.slots);
  const open = slots.filter((slot) => slot.available).length;
  return { open, total: slots.length, share: slots.length > 0 ? open / slots.length : 0 };
};

/** How far through its day a time falls on the given clock, 0 at midnight to 1 at the next. */
export const dayShare = (startAt: string, timeZone: string) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone,
  }).formatToParts(new Date(startAt));
  const read = (type: "hour" | "minute") => Number(parts.find((part) => part.type === type)?.value ?? 0);
  return (read("hour") * 60 + read("minute")) / 1440;
};

/** "Today" for today, "Tue" for any other day, on the given clock. */
export const formatOpenDay = (
  startAt: string,
  {
    timeZone,
    todayLabel,
    now = new Date(),
    locale,
  }: { timeZone: string; todayLabel: string; now?: Date; locale?: string },
) => {
  const dayKey = (date: Date) => new Intl.DateTimeFormat("en-CA", { timeZone }).format(date);
  const date = new Date(startAt);
  return dayKey(date) === dayKey(now)
    ? todayLabel
    : new Intl.DateTimeFormat(locale, { weekday: "long", timeZone }).format(date);
};

/** What a visitor calls a place: its gym, its own name, else its street. */
export const placeName = (place: PublicPlace) => place.gym?.name || place.name || place.address || place.city;

/** The places one coach works at, in the business's order. Someone with no hours yet is at the main one. */
export const coachPlaces = (directory: Pick<PublicDirectory, "places">, specialist: PublicSpecialist) => {
  const places = directory.places ?? [];
  const ids = specialist.locationIds ?? [];
  return ids.length > 0 ? places.filter((place) => ids.includes(place.id)) : places.filter((place) => place.main);
};

/** Names in a row, the first three and then how many more: "Francuska · Mokotów · Wola +2". */
export const placeList = (places: PublicPlace[], more: (count: number) => string) => {
  const names = places.map(placeName).filter(Boolean);
  return names.length > 3 ? `${names.slice(0, 3).join(" · ")} ${more(names.length - 3)}` : names.join(" · ");
};
